import _StackFrame      from 'stackframe';
import ErrorStackParser from 'error-stack-parser';
import StacktraceGPS    from 'stacktrace-gps';
export class StackFrame extends _StackFrame {
	constructor(options : _StackFrame.StackFrameOptions) {
		super(options);
	};
};
const gps = new StacktraceGPS;
export class StackFrames {
	static async fromError(error : Error) {
		const frames = await Promise.all(
			ErrorStackParser.parse(error).
			map(async (frame) => {
					try {
						return (await gps.pinpoint(frame));
					} catch (e) {
					}
					return (frame);
			})
		);
		const sf = new StackFrames;
		sf.frames.push(...frames);
		return (sf);
	};
	static async fromStack(stack : string) {
		return (await this.fromError(<Error>{
			stack
		}));
	};
	static async fromPosition() {
		const stackFrames = await this.fromError(new Error);
		stackFrames.frames = stackFrames.frames.slice(2);
		return (stackFrames);
	};
	frames : StackFrame[] = [];
};
export interface IErrorDetails {
	[ key          : string ] : unknown;
	boundError?    : IErrorExtended; // Error who caused this error
	boundEvent?    : Event; // Event who caused this error
	bound?         : unknown; // Something undetermined who caused this error
	isUserError    : boolean;
	isLibraryError : boolean;
	/** @deprecated **/
	boundTo? : unknown;
};
export interface IErrorExtended extends Error {
	details? : IErrorDetails;
};
export interface IErrorExtendedPartial extends Error {
	details? : Partial<IErrorDetails>;
};
const all : IErrorExtended[]      = [];
let last : IErrorExtended | null = null;
const createError = (
	Error  : ErrorConstructor,
	msg    : string,
	data0? : Partial<IErrorDetails>,
	data1? : {
		isUserError    : boolean;
		isLibraryError : boolean;
	}
) => {
	const error : IErrorExtended = new Error(msg);
	error.details = Object.assign({}, data0, data1);
	addError(error);
	return (error);
};
const createInfo = async (error : IErrorExtended) => {
	var out : {
		name       : string;
		message    : string;
		type       : string;
		stacktrace : StackFrames['frames'];
		details?   : IErrorDetails;
	} = {
		name       : error.name,
		message    : error.message,
		type       : 'native',
		stacktrace : (await StackFrames.fromError(error)).frames
	};
	if (error.details) {
		const details : Partial<IErrorDetails> = {};
		for (const [ key, value ] of Object.entries(error.details)) {
			if (key === 'isLibraryError') {
				if (value) {
					out.type = 'library';
				}
			} else if (key === 'isUserError') {
				if (value) {
					out.type = 'user';
				}
			} else if (key !== 'boundTo') {
				details[ key ] = value;
			}
		}
		if (Object.keys(details).length > 0) {
			out.details = <IErrorDetails>details;
		}
	}
	return (out);
};
const onError = (event : ErrorEvent) => {
	let error : Error | null = null;
	const details : Partial<IErrorExtended['details']> = {};
	if ('error' in event) {
		if (event.error instanceof Error) {
			error = event.error;
		} else if (event.error instanceof Event) {
			details.boundEvent = event.error;
		} else {
			details.bound = event.error;
		}
	} else {
		details.boundEvent = event;
	}
	if (!error) {
		error = library('Unknown error!', details);
	}
	addError(error);
};
if (globalThis.window) { // make compatible with nodejs
	window.addEventListener('error', onError);
}
export function user(
	msg   : string,
	data? : Partial<IErrorDetails>
) {
	return (createError(Error, msg, data, {
		isUserError    : true,
		isLibraryError : false
	}));
};
export function library(
	msg   : string,
	data? : Partial<IErrorDetails>
) {
	return (createError(Error, msg, data, {
		isUserError    : false,
		isLibraryError : true
	}));
};
export function bind(
	msg        : string,
	data       : Partial<IErrorDetails>,
	boundError : IErrorExtended | Event
) {
	const boundErrorIsAnError = boundError instanceof Error;
	userAssert(
		boundErrorIsAnError || (boundError instanceof Event),
		'Bound error is not an valid binding!',
		{
			bound : boundError
		}
	);
	const category = (
		(
			data.isUserError ||
			(boundErrorIsAnError && boundError.details?.isUserError)
		) ? 'user' : 'library'
	);
	const details : Partial<IErrorDetails> = Object.assign({}, data);
	if (boundErrorIsAnError) {
		details.boundError = boundError;
	} else {
		details.boundEvent = boundError;
	}
	const error = (category === 'user') ? user(msg, details) : library(msg, details);
	all.splice(all.indexOf(error), 1);
	return (error);
};
export function bindFromUnknown(
	message    : string,
	data       : Partial<IErrorDetails>,
	boundError : unknown
) {
	if ((boundError instanceof Error) || (boundError instanceof Event)) {
		return (bind(message, data, boundError));
	}
	const fakeError : IErrorExtended = new Error('Unknown error');
	const isUserError = data.isUserError === true;
	fakeError.details = {
		bound          : boundError,
		isLibraryError : !isUserError,
		isUserError
	};
	return (bind(message, data, fakeError));
};
export function clear() {
	all.length = 0;
	last = null;
};
export async function display(err : IErrorExtended) {
	const infos = (
		(await summarize(err))
		.map(x => `  ${ x.type }: ${ x.name }: ${ x.message }`)
	);
	console.error(infos.join('\n'));
	console.error(err.stack);
};
export function handle(
	error   : IErrorExtended | null,
	_switch : {
		success?      : () => void;
		userError?    : () => void;
		libraryError? : () => void;
	}
) {
	if (!error) {
		if (_switch.success) {
			_switch.success();
		}
	} else {
		if ((<IErrorExtended>error)?.details?.isUserError) {
			if (_switch.userError !== undefined) {
				_switch.userError();
			}
		} else {
			if (_switch.libraryError !== undefined) {
				_switch.libraryError();
			}
		}
	}
};
export async function summarize(error : IErrorExtended) {
	var	out = [];
	let currentError : IErrorExtended | null = error;
	while (currentError) {
		out.push(await createInfo(currentError));
		currentError = currentError?.details?.boundError ||  null;
	}
	if (out.length === 1) {
		return (out);
	}
	return (out.reverse());
};
export async function summarizeLastError() {
	const error = getLast();
	if (!error) {
		throw user('There no error to summarize!');
	}
	return (await summarize(error));
};
export function getBoundAt(baseError : IErrorExtended, baseIdx : number) {
	let error : IErrorExtended | null = baseError;
	let idx   = baseIdx;
	while (idx-- !== 0) {
		if (
			!error.details ||
			!error.details.boundError
		) {
			throw user(
				(
					`Trying to get a bound error at index<${ idx } ` +
					`from error<${ baseError }> but there no error at this path!`
				),
				{
					baseError,
					baseIdx
				}
			);
		}
		error = error.details.boundError;
	}
	return (error);
};
export function addError(error : IErrorExtended) {
	all.push(error);
	last = error;
};
export const getFromName = (() => {
	const errors = Object.freeze(
		[
			Error,
			EvalError,
			RangeError,
			ReferenceError,
			SyntaxError,
			TypeError
		]
		.reduce((a, c) => {
			a[ c.name ] = c;
			return (a);
		}, <Record<string, unknown>>{})
	);
	return ((name : string) => errors[name]);
})();
export function getLast() {
	return (last);
};
export function getAll() {
	return (all);
};
export function createErrorFromUnknown(from : unknown) {
	if (from instanceof Error) {
		return (from);
	} else {
		if (typeof from === 'string') {
			return (new Error('Unknown error: ' + from));
		}
		return (library('Unknown error', { from }));
	}
};
export function removeHandledError(error : IErrorExtended) {
	all.splice(all.indexOf(error), 1);
	if (last === error) {
		last = all[all.length - 1] || null;
	}
};
export function userAssert(condition : any, ...args : Parameters<typeof user>) : asserts condition {
	if (!condition) {
		throw user(...args);
	}
};
export function libraryAssert(condition : any, ...args : Parameters<typeof library>) : asserts condition {
	if (!condition) {
		throw library(...args);
	}
};
